/**
 * Polls the server for a due check-in and shows the modal.
 *
 * The server owns the schedule; this component only asks "is anything due?".
 * Poll cadence adapts to how far away the next ping is, so an idle afternoon
 * costs a handful of requests rather than one every fifteen seconds.
 */
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from '../api/client';
import type { PendingPing } from '../types';
import PingModal from './PingModal';
import { beep, notifyCheckIn } from '../lib/checkInAlarm';

const FAST_POLL_MS = 15_000;
const SLOW_POLL_MS = 60_000;

export default function PingWatcher() {
  const queryClient = useQueryClient();

  const { data } = useQuery<PendingPing>({
    queryKey: ['pending-ping'],
    queryFn: async () => (await client.get('/api/v1/attendance/ping/pending')).data,
    refetchInterval: (query) => {
      const result = query.state.data;
      if (!result?.sessionOpen) return false; // no shift, nothing to watch
      if (result.ping) return FAST_POLL_MS;

      const seconds = result.secondsUntilNext;
      if (seconds == null) return SLOW_POLL_MS;
      // Wake up shortly before the next ping is due, but never sleep so long
      // that a due prompt sits unnoticed.
      return Math.min(Math.max(seconds * 1000 * 0.5, FAST_POLL_MS), SLOW_POLL_MS);
    },
    refetchOnWindowFocus: true,
    retry: 1,
  });

  /**
   * Sound and notify on the edge, not on every poll.
   *
   * Once when the prompt appears, once more two minutes later if it is still
   * unanswered, then silence. The prompt cannot be dismissed and ignoring it
   * costs an availability score, so a single beep is too easy to miss — but a
   * beep every fifteen seconds is how somebody mutes the tab, which would kill
   * every signal at once.
   */
  const alarmed = useRef<string | null>(null);
  const repeat = useRef<number | null>(null);

  useEffect(() => {
    const pingId = data?.ping?.id ?? null;

    if (pingId === null) {
      alarmed.current = null;
      if (repeat.current) window.clearTimeout(repeat.current);
      repeat.current = null;
      return;
    }
    if (alarmed.current === pingId) return;

    alarmed.current = pingId;
    beep();
    notifyCheckIn();

    repeat.current = window.setTimeout(() => {
      // Only if this same prompt is still the one waiting.
      if (alarmed.current === pingId) {
        beep();
        notifyCheckIn();
      }
    }, 120_000);

    return () => {
      if (repeat.current) window.clearTimeout(repeat.current);
    };
  }, [data?.ping?.id]);

  // A returning tab should learn about a waiting prompt immediately rather
  // than at the next scheduled poll.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [queryClient]);

  // Surface the prompt in the tab title so it is visible from another tab.
  useEffect(() => {
    const original = document.title;
    if (data?.ping) document.title = '● Check-in required — ATS India';
    return () => {
      document.title = original;
    };
  }, [data?.ping]);

  if (!data?.ping) return null;

  return (
    <PingModal
      ping={data.ping}
      onAnswered={() => {
        queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
        queryClient.invalidateQueries({ queryKey: ['todays-pings'] });
        queryClient.invalidateQueries({ queryKey: ['my-attendance'] });
      }}
    />
  );
}
